/* import __COLOCATED_TEMPLATE__ from './schedule-button.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import type ArticleContent from 'embercom/models/articles/article-content';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from 'tracked-built-ins';
import { MULTILINGUAL_ARTICLES_FEATURE, ARTICLES_FEATURE } from 'embercom/lib/billing';
import { type ScheduleMode } from 'embercom/lib/knowledge-hub/constants';

interface Signature {
  Element: HTMLElement;
  Args: Args;
}

export interface Args {
  activeContent: ArticleContent;
  scheduleActiveContentUpdate?: (params: {
    mode: ScheduleMode;
    scheduledDate: string;
    timezone?: string;
  }) => Promise<void>;
  inRightList?: boolean;
  disabled?: boolean;
  isPublishing?: boolean;
  paywallIsActive?: boolean;
  mode?: ScheduleMode;
}

export default class ScheduleButton extends Component<Signature> {
  @service declare appService: AppService;

  @tracked showScheduleModal = false;

  get canUseScheduledPublishing() {
    return this.appService.app.canUseScheduledPublishing;
  }

  get isUnpublishMode() {
    return this.args.mode === 'unpublish';
  }

  get paywallConfig() {
    // Paywall only applies to publish mode
    if (this.isUnpublishMode) {
      return undefined;
    }
    let configs = this.args.activeContent.editorConfig.paywallConfig;
    if (configs) {
      if (!this.args.activeContent.isInDefaultLocale) {
        return configs.find((config) => config.featureKey === MULTILINGUAL_ARTICLES_FEATURE);
      }
      return configs.find((config) => config.featureKey === ARTICLES_FEATURE);
    }
    return undefined;
  }

  isButtonDisabled = (paywallIsActive = false) => {
    if (this.isUnpublishMode) {
      return this.args.isPublishing || !this.args.activeContent.isPublished || this.args.disabled;
    }
    return (
      this.args.isPublishing ||
      this.args.activeContent.isPublishDisabled ||
      this.args.disabled ||
      paywallIsActive
    );
  };

  @action async scheduleActiveContentUpdate(params: { scheduledDate: string; timezone?: string }) {
    if (this.args.scheduleActiveContentUpdate) {
      let mode = this.args.mode || 'publish';
      await this.args.scheduleActiveContentUpdate({
        mode,
        scheduledDate: params.scheduledDate,
        timezone: params.timezone,
      });
    }
  }

  @action toggleScheduleModal(state = false): void {
    this.showScheduleModal = state;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::ArticleContent::ScheduleButton': typeof ScheduleButton;
    'knowledge-hub/content-editor/article-content/schedule-button': typeof ScheduleButton;
  }
}
